import { useIdentity } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavBar from '../components/NavBar';

interface AvailableOrganization {
  _type: string;
  organization_id: string;
  organization_name: string;
}

const AuthEmailConfirmedPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useReactQuery();
  const { addOrganizationAuth } = useIdentity();

  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState<AvailableOrganization[]>(
    [],
  );
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the token from URL parameters
  const token = searchParams.get('token');

  // Create mutation hooks for the v2 auth APIs
  const listOrganizations = apiClient.useMutation(
    'post',
    '/v2/auth/list-available-organizations',
  );

  const authenticateOrganization = apiClient.useMutation(
    'post',
    '/v2/auth/authenticate-organization',
  );

  // Load available organizations on component mount
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!token) {
        setError('Invalid authentication token. Please try signing in again.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await listOrganizations.mutateAsync({
          body: {
            token: token,
          },
        });

        if (response.organizations) {
          setOrganizations(response.organizations);
          // Auto-select if only one organization
          if (response.organizations.length === 1) {
            setSelectedOrganizationId(
              response.organizations[0].organization_id,
            );
          }
        } else {
          setError('No organizations found for your account.');
        }
      } catch (err) {
        console.error('Error loading organizations:', err);
        setError('Failed to load organizations. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganizations();
  }, [token]);

  const handleOrganizationSelect = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
  };

  const handleAuthenticate = async () => {
    if (!selectedOrganizationId || !token) return;

    setIsAuthenticating(true);
    setError(null);

    try {
      const response = await authenticateOrganization.mutateAsync({
        body: {
          organization: selectedOrganizationId,
          token: token,
        },
      });

      if (response.accessToken && response.refreshToken) {
        // Store tokens per organization using the new organization-aware auth
        const selectedOrg = organizations.find(
          (org) => org.organization_id === selectedOrganizationId,
        );

        addOrganizationAuth(selectedOrganizationId, {
          organization_id: selectedOrganizationId,
          organization_name: selectedOrg?.organization_name,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });

        // Show signing in animation
        setIsSigningIn(true);

        // Wait 200ms then force refresh the page
        setTimeout(() => {
          window.location.href = '/';
        }, 200);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      console.error('Error authenticating with organization:', err);
      setError(
        'Failed to authenticate with selected organization. Please try again.',
      );
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Show signing in animation
  if (isSigningIn) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Signing in animation */}
            <div className="mx-auto w-16 h-16 mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600"></div>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Signing in...
            </h1>

            <p className={`text-xl ${provideTailwind('text-secondary')}`}>
              Please wait while we complete your sign in.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Loading spinner */}
            <div className="mx-auto w-16 h-16 mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Loading your organizations...
            </h1>

            <p className={`text-xl ${provideTailwind('text-secondary')}`}>
              Please wait while we fetch your available organizations.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Error icon */}
            <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Something went wrong
            </h1>

            <p className={`text-xl mb-8 ${provideTailwind('text-secondary')}`}>
              {error}
            </p>

            <button
              onClick={() => navigate('/signin-v2')}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${provideTailwind(
                'bg-secondary text-primary hover:bg-opacity-80',
              )}`}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <NavBar />

      <div className="max-w-2xl mx-auto px-8 py-16">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl font-bold mb-6 ${provideTailwind(
              'text-primary',
            )}`}
          >
            Select your organization
          </h1>

          <p className={`text-xl ${provideTailwind('text-secondary')}`}>
            Choose which organization you'd like to sign in to:
          </p>
        </div>

        <div className="space-y-4 mb-8">
          {organizations.map((org) => (
            <div
              key={org.organization_id}
              className={`p-6 rounded-lg border-2 cursor-pointer transition-colors ${
                selectedOrganizationId === org.organization_id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
              } ${provideTailwind('bg-secondary')}`}
              onClick={() => handleOrganizationSelect(org.organization_id)}
            >
              <div className="flex items-center">
                <div
                  className={`w-4 h-4 rounded-full border-2 mr-4 ${
                    selectedOrganizationId === org.organization_id
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {selectedOrganizationId === org.organization_id && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
                <div>
                  <h3
                    className={`text-lg font-semibold ${provideTailwind(
                      'text-primary',
                    )}`}
                  >
                    {org.organization_name}
                  </h3>
                  <p className={`text-sm ${provideTailwind('text-secondary')}`}>
                    Organization ID: {org.organization_id}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        <button
          onClick={handleAuthenticate}
          disabled={!selectedOrganizationId || isAuthenticating}
          className={`w-full py-4 px-4 bg-pink-600 text-white text-lg font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center`}
        >
          {isAuthenticating ? (
            <svg
              className="animate-spin h-5 w-5 text-white mr-2"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : null}
          {isAuthenticating ? 'Signing In...' : 'Sign In'}
        </button>
      </div>
    </div>
  );
};

export default AuthEmailConfirmedPage;
